import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Save, X, Search, Dumbbell, Sparkles } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { exercisesAPI } from '../../lib/api';
import { disciplineHasHint, isRecommendedExercise } from '../../lib/disciplineExercises';
import { disciplineMeta } from '../../lib/disciplines';
import type { BlockItem, BlockItemFormData, Exercise, GroupedExercises, Discipline } from '../../types';
import clsx from 'clsx';

interface BlockItemFormProps {
  blockId: string;
  discipline?: Discipline | null;
  initialData?: BlockItem;
  onSubmit: (data: BlockItemFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function BlockItemForm({ discipline, initialData, onSubmit, onCancel, isLoading }: BlockItemFormProps) {
  const [formData, setFormData] = useState<BlockItemFormData>({
    order: initialData?.order ?? 0,
    sets: initialData?.sets || undefined,
    reps: initialData?.reps || '',
    weight: initialData?.weight || '',
    tempo: initialData?.tempo || '',
    restSeconds: initialData?.restSeconds || undefined,
    notes: initialData?.notes || '',
    exerciseId: initialData?.exerciseId || '',
  });

  const [exerciseSearch, setExerciseSearch] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<string>('');
  // Sesgo por disciplina: arranca activo si la disciplina tiene recomendación.
  const hasHint = disciplineHasHint(discipline);
  const [recommendedOnly, setRecommendedOnly] = useState(hasHint);

  const { data: groupedExercises, isLoading: loadingExercises } = useQuery({
    queryKey: ['exercises', 'grouped'],
    queryFn: () => exercisesAPI.getGrouped(),
  });

  const { data: filters } = useQuery({
    queryKey: ['exercises', 'filters'],
    queryFn: () => exercisesAPI.getFilters(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.exerciseId) {
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const selectExercise = (exercise: Exercise) => {
    setFormData(prev => ({ ...prev, exerciseId: exercise.id }));
  };

  // Filter exercises based on search and muscle group
  const getFilteredExercises = (): GroupedExercises => {
    if (!groupedExercises) return {};

    const filtered: GroupedExercises = {};
    
    Object.entries(groupedExercises as GroupedExercises).forEach(([key, group]) => {
      if (selectedMuscleGroup && key !== selectedMuscleGroup) return;

      const matchingExercises = group.exercises.filter(ex =>
        ex.name.toLowerCase().includes(exerciseSearch.toLowerCase()) &&
        (!recommendedOnly || isRecommendedExercise(discipline, ex))
      );

      if (matchingExercises.length > 0) {
        filtered[key] = { ...group, exercises: matchingExercises };
      }
    });

    return filtered;
  };

  const filteredExercises = getFilteredExercises();
  const selectedExercise = initialData?.exercise || 
    Object.values(filteredExercises).flatMap(g => g.exercises).find(e => e.id === formData.exerciseId);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Exercise Selection */}
      <div>
        <label className="label">Ejercicio *</label>
        
        {selectedExercise ? (
          <div className="flex items-center justify-between p-3 rounded-xl bg-primary-500/10 border border-primary-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-dark-100">{selectedExercise.name}</p>
                <p className="text-xs text-dark-400">
                  {selectedExercise.muscleGroupLabel} • {selectedExercise.categoryLabel}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, exerciseId: '' }))}
              className="p-1.5 rounded-lg text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sesgo por disciplina del plan */}
            {hasHint && (
              <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                <input
                  type="checkbox"
                  checked={recommendedOnly}
                  onChange={(e) => setRecommendedOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                />
                <Sparkles className="w-3.5 h-3.5 text-primary-400" />
                <span className="text-dark-300">
                  Recomendados para {disciplineMeta(discipline)?.label}
                </span>
              </label>
            )}

            {/* Search and Filter */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
                <input
                  type="text"
                  placeholder="Buscar ejercicio..."
                  value={exerciseSearch}
                  onChange={(e) => setExerciseSearch(e.target.value)}
                  className="input pl-9 py-2 text-sm"
                />
              </div>
              <select
                value={selectedMuscleGroup}
                onChange={(e) => setSelectedMuscleGroup(e.target.value)}
                className="input py-2 text-sm w-auto"
              >
                <option value="">Todos</option>
                {filters?.muscleGroups?.map((mg: { value: string; label: string }) => (
                  <option key={mg.value} value={mg.value}>{mg.label}</option>
                ))}
              </select>
            </div>

            {/* Exercise List */}
            <div className="max-h-48 overflow-y-auto rounded-xl border border-dark-700 bg-dark-800/50">
              {loadingExercises ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                </div>
              ) : Object.keys(filteredExercises).length > 0 ? (
                <div className="divide-y divide-dark-700">
                  {Object.entries(filteredExercises).map(([key, group]) => (
                    <div key={key}>
                      <div className="px-3 py-1.5 bg-dark-900/50 text-xs font-medium text-dark-400 sticky top-0">
                        {group.label}
                      </div>
                      <div className="divide-y divide-dark-700/50">
                        {group.exercises.map((exercise: Exercise) => (
                          <button
                            key={exercise.id}
                            type="button"
                            onClick={() => selectExercise(exercise)}
                            className={clsx(
                              'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                              formData.exerciseId === exercise.id
                                ? 'bg-primary-500/10'
                                : 'hover:bg-dark-700'
                            )}
                          >
                            <Dumbbell className="w-3.5 h-3.5 text-dark-500 flex-shrink-0" />
                            <span className="text-sm text-dark-200 truncate">{exercise.name}</span>
                            <span className="text-xs text-dark-500 ml-auto flex-shrink-0">
                              {exercise.categoryLabel}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-6 text-dark-400 text-sm">
                  No se encontraron ejercicios
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Exercise Details */}
      {formData.exerciseId && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label htmlFor="sets" className="label">Series</label>
              <input
                type="number"
                id="sets"
                name="sets"
                value={formData.sets || ''}
                onChange={handleChange}
                min={1}
                className="input py-2"
                placeholder="4"
              />
            </div>
            <div>
              <label htmlFor="reps" className="label">Repeticiones</label>
              <input
                type="text"
                id="reps"
                name="reps"
                value={formData.reps}
                onChange={handleChange}
                className="input py-2"
                placeholder="8-12"
              />
            </div>
            <div>
              <label htmlFor="weight" className="label">Carga</label>
              <input
                type="text"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                className="input py-2"
                placeholder="RPE 8"
              />
            </div>
            <div>
              <label htmlFor="restSeconds" className="label">Descanso (seg)</label>
              <input
                type="number"
                id="restSeconds"
                name="restSeconds"
                value={formData.restSeconds || ''}
                onChange={handleChange}
                min={0}
                className="input py-2"
                placeholder="90"
              />
            </div>
          </div>

          <div>
            <label htmlFor="tempo" className="label">Tempo (opcional)</label>
            <input
              type="text"
              id="tempo"
              name="tempo"
              value={formData.tempo}
              onChange={handleChange}
              className="input py-2"
              placeholder="3-1-1-0 (excéntrico-pausa-concéntrico-pausa)"
            />
          </div>

          <div>
            <label htmlFor="notes" className="label">Notas</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="input resize-none"
              placeholder="Indicaciones especiales..."
            />
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button 
          type="submit" 
          disabled={isLoading || !formData.exerciseId} 
          className="btn-primary"
        >
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar' : 'Agregar'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

